import { css } from '@emotion/react'
import { ConsultaSeguimentoArrowDown } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoArrowDown'
import { ConsultaSeguimentoIndicatorHelper } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeaderTitle'
import { PerguntaSimNaoRadioGroupBoxField } from '@mchat-rf/consulta-seguimento/components/PerguntaSimNaoRadioGroupBoxField'
import { PerguntaSimNaoRadioListField } from '@mchat-rf/consulta-seguimento/components/PerguntaSimNaoRadioListField'
import { isPerguntaStatusRespondendo } from '@mchat-rf/consulta-seguimento/consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { useConsultaSeguimentoAutoScroll } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoAutoScroll'
import { useConsultaSeguimentoSetResultado } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoSetResultado'
import { hasAnySim } from '@mchat-rf/consulta-seguimento/utils-consultaSeguimento'
import { Flow, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { RefObject } from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import {
  ConsultaSeguimentoPergunta08ForaAlcanceEnum,
  ConsultaSeguimentoPergunta08Model,
} from './model-consultaSeguimentoPergunta08'
import { PERGUNTA_08_SCROLL_CONFIG } from './scrollConfig-consultaSeguimentoPergunta08'
import {
  calculateResultadoPergunta08,
  conditionToCalculatePergunta08,
  isPergunta08ForaAlcanceAllNao,
} from './utils-consultaSeguimentoPergunta08'

interface ConsultaSeguimentoPergunta08FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta08Model>
  containerRef: RefObject<HTMLDivElement>
  goNextButtonRef: RefObject<HTMLButtonElement>
}

export function ConsultaSeguimentoPergunta08Field(props: Readonly<ConsultaSeguimentoPergunta08FieldProps>) {
  const { name, containerRef, goNextButtonRef } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta08Model>(resolveName(name), {
    subscription: { value: true },
  })

  const notEndAt02 =
    value.PERGUNTA_08_HEADER === SimNaoEnum.NAO ||
    (value.PERGUNTA_08_HEADER === SimNaoEnum.SIM &&
      value.PERGUNTA_08_SE_INTERESSA_POR &&
      value.PERGUNTA_08_SE_INTERESSA_POR !== SimNaoEnum.SIM)

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta08Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta08,
    calculateResultado: calculateResultadoPergunta08,
  })

  const { getId, triggerScrollFor } = useConsultaSeguimentoAutoScroll<ConsultaSeguimentoPergunta08Model>({
    name,
    containerRef,
    goNextButtonRef,
    scrollConfig: PERGUNTA_08_SCROLL_CONFIG,
  })

  return (
    <VFlow>
      {isPerguntaStatusRespondendo(value.status) && (
        <ConsultaSeguimentoPerguntaHeader
          pergunta={MChatPerguntaEnum.PERGUNTA_08}
          title={<ConsultaSeguimentoPerguntaHeaderTitle sufix=' se interessa por outras crianças?' />}
          status={value.status}
          field={
            <SimNaoSwitchButtonField
              name={name.PERGUNTA_08_HEADER}
              onChange={() => triggerScrollFor('PERGUNTA_08_HEADER')}
            />
          }
        />
      )}
      {value.PERGUNTA_08_HEADER && (
        <VFlow vSpacing={0}>
          <ConsultaSeguimentoIndicatorStart />
          {value.PERGUNTA_08_HEADER === SimNaoEnum.SIM && (
            <div id={getId('PERGUNTA_08_SE_INTERESSA_POR')}>
              <PerguntaSimNaoRadioGroupBoxField
                name={name.PERGUNTA_08_SE_INTERESSA_POR}
                label='Ele se interessa por crianças que não são irmãos dele?'
                onChange={() => triggerScrollFor('PERGUNTA_08_SE_INTERESSA_POR')}
                required
              />
            </div>
          )}
          {notEndAt02 && (
            <Flow direction='vertical' gap={0}>
              {value.PERGUNTA_08_HEADER === SimNaoEnum.SIM && <ConsultaSeguimentoArrowDown />}
              <div id={getId('PERGUNTA_08_RESPONDE_PERGUNTAS')}>
                <PerguntaSimNaoRadioGroupBoxField
                  name={name.PERGUNTA_08_RESPONDE_PERGUNTAS}
                  label='Quando vocês estão na pracinha ou no supermercado, seu filho geralmente responde perguntas de outras crianças?'
                  onChange={() => triggerScrollFor('PERGUNTA_08_RESPONDE_PERGUNTAS')}
                  required
                />
              </div>
              {value.PERGUNTA_08_RESPONDE_PERGUNTAS === SimNaoEnum.SIM && (
                <Flow direction='vertical' gap={0} id={getId('PERGUNTA_08_FORA_ALCANCE')}>
                  <ConsultaSeguimentoArrowDown />
                  <ConsultaSeguimentoIndicatorHelper
                    title='Como é que seu filho responde?'
                    subtitle='(Se o genitor não der nenhum dos exemplos abaixo, perguntar cada um deles individualmente)'
                  />
                  <VFlow vSpacing={0} style={styles.perguntaOlharAlgoInteressante}>
                    <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta08ForaAlcanceEnum>
                      name={name.PERGUNTA_08_FORA_ALCANCE}
                      label='Seu filho...'
                      description='Responda os exemplos até pontuar na Pergunta 8 ou avançar para o próximo fluxo'
                      options={PERGUNTA_08_SEU_FILHO_OPTIONS}
                      onChange={() => triggerScrollFor('PERGUNTA_08_FORA_ALCANCE')}
                      required
                    />
                    {hasAnySim(value?.PERGUNTA_08_FORA_ALCANCE) && (
                      <Flow direction='vertical' gap={0} id={getId('PERGUNTA_08_RESPONDE_CRIANCAS')}>
                        <ConsultaSeguimentoIndicatorStep text='Sim para qualquer um:' />
                        <PerguntaSimNaoRadioGroupBoxField
                          name={name.PERGUNTA_08_RESPONDE_CRIANCAS}
                          label='Ele responde a outras crianças mais da metade das vezes?'
                          onChange={() => triggerScrollFor('PERGUNTA_08_RESPONDE_CRIANCAS')}
                          required
                        />
                      </Flow>
                    )}
                  </VFlow>
                </Flow>
              )}
            </Flow>
          )}
          {value.resultado && (
            <ConsultaSeguimentoIndicatorResultado
              text={{
                FALHOU: isPergunta08ForaAlcanceAllNao(value.PERGUNTA_08_FORA_ALCANCE) ? 'Não para todos:' : undefined,
              }}
              resultado={value.resultado}
            />
          )}
        </VFlow>
      )}
    </VFlow>
  )
}

const PERGUNTA_08_SEU_FILHO_OPTIONS: RadioOption<ConsultaSeguimentoPergunta08ForaAlcanceEnum>[] = [
  {
    label: 'Brinca com outra criança?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.BRINCA_COM_OUTRA_CRIANCA,
  },
  {
    label: 'Fala com outra criança?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.FALA_COM_OUTRA_CRIANCA,
  },
  {
    label: 'Balbucia ou faz barulhos com a boca?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.BALBUCIA_FAZ_BARULHO,
  },
  {
    label: 'Olha para outra criança?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.OLHA_PRA_OUTRA_CRIANCA,
  },
  {
    label: 'Sorri para outra criança?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.SORRI_PRA_OUTRA_CRIANCA,
  },
  {
    label: 'Primeiro fica tímido, mas depois sorri?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.FICA_TIMIDO,
  },
  {
    label: 'Fica animado com a outra criança?',
    value: ConsultaSeguimentoPergunta08ForaAlcanceEnum.FICA_ANIMADO,
  },
]

const styles = {
  perguntaOlharAlgoInteressante: css`
    margin-top: 1rem;
  `,
}
